import { ApiTagTypes, baseApi } from './baseApi';
import { BioLibSingleton, IApiToken, IUserDeprecated, Paginated } from '@biolibtech/biolib-js';
import { IUserUpdate, IUserDetailed, IUserGroup } from './userApi.types';
import AuthCookie from '../features/userFlow/utils/AuthCookie';

const userApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        getCurrentUser: build.query<IUserDetailed, void>({
            providesTags: [ApiTagTypes.user, ApiTagTypes.account],
            query: () => ({
                url: '/users/me/',
            }),
        }),
        getUser: build.query<IUserDeprecated, void>({
            providesTags: [ApiTagTypes.user, ApiTagTypes.account],
            query: () => ({
                url: `/user/${BioLibSingleton.get().httpClient.getSignedInUserId()}/`,
            }),
        }),
        updateUser: build.mutation<IUserDeprecated, IUserUpdate>({
            invalidatesTags: [ApiTagTypes.user],
            query: (args) => ({
                body: args.body,
                method: 'PATCH',
                url: `/user/${args.uuid}/`,
            }),
        }),
        requestVerificationEmail: build.mutation<{ email: string }, void>({
            query: () => ({
                method: 'GET',
                url: `/user/verify_email`,
            }),
        }),
        passwordReset: build.mutation<void, { email: string }>({
            query: (args) => ({
                body: { email: args.email },
                method: 'POST',
                url: `/user/password_reset/`,
            }),
        }),
        getUserApiTokens: build.query<Paginated<IApiToken>, { page?: number; pageSize?: number }>({
            providesTags: [ApiTagTypes.tokens],
            query: (args) => ({
                url: `/user/api_tokens`,
                params: { page: args?.page, page_size: args?.pageSize },
            }),
        }),
        createApiToken: build.mutation<IApiToken, { name: string }>({
            invalidatesTags: [ApiTagTypes.tokens],
            query: (args) => ({
                body: { name: args.name },
                method: 'POST',
                url: `/user/api_tokens/`,
            }),
        }),
        revokeApiToken: build.mutation<void, { apiTokenId: string }>({
            invalidatesTags: [ApiTagTypes.tokens],
            query: (args) => ({
                method: 'DELETE',
                url: `/user/api_tokens/${args.apiTokenId}/`,
            }),
        }),
        updateApiToken: build.mutation<IApiToken, { apiTokenId: string; name: string }>({
            invalidatesTags: [ApiTagTypes.tokens],
            query: (args) => ({
                body: { name: args.name },
                method: 'PATCH',
                url: `/user/api_tokens/${args.apiTokenId}/`,
            }),
        }),
        getUserGroups: build.query<Paginated<IUserGroup>, { page?: number; pageSize?: number }>({
            providesTags: [ApiTagTypes.user, ApiTagTypes.account],
            query: (args) => ({
                url: `/users/me/groups/`,
                params: { page: args?.page, page_size: args?.pageSize },
            }),
        }),
    }),
});

export const {
    useCreateApiTokenMutation,
    useRevokeApiTokenMutation,
    useGetCurrentUserQuery,
    useGetUserApiTokensQuery,
    useGetUserGroupsQuery,
    useGetUserQuery,
    usePasswordResetMutation,
    useRequestVerificationEmailMutation,
    useUpdateApiTokenMutation,
    useUpdateUserMutation,
} = userApi;

export function useIsSignedIn(): boolean {
    const { data: user, isLoading } = useGetCurrentUserQuery();

    if (isLoading) {
        return AuthCookie.isSignedIn();
    }

    return !!user;
}
